import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from "react-router-dom";
import GamePage from './game.jsx';
import { useUser } from '../UserContext.js';
import { db } from '../auth.js';
import { collection, getDocs, query, where } from "firebase/firestore";

// Cache: all users with projects (light read), and all released projects (lazy)
let cachedUsers = null;
let cachedAllProjects = null;
let loadingAllProjects = false;

function pickRandom(arr) {
    return arr[Math.floor(Math.random() * arr.length)];
}

// Fetch user list (light: only reads user collection once)
async function fetchUsers() {
    if (cachedUsers) return cachedUsers;
    const snapshot = await getDocs(collection(db, "user"));
    const users = [];
    snapshot.forEach(doc => {
        if (doc.data().projectNum > 0) users.push({ uid: doc.id, name: doc.data().name });
    });
    cachedUsers = users;
    return users;
}

// Pick one random released project from a random user (fast: 1-2 queries)
async function fetchOneRandomProject(excludeProjectId) {
    const users = await fetchUsers();
    if (users.length === 0) return null;

    const shuffled = [...users].sort(() => Math.random() - 0.5);
    for (const u of shuffled) {
        const q = query(collection(db, "user", u.uid, 'project'), where("release", "==", true));
        const snapshot = await getDocs(q);
        const projects = [];
        snapshot.forEach(doc => {
            projects.push({ userid: u.uid, userName: u.name, projectId: doc.id, projectName: doc.data().name });
        });
        const candidates = excludeProjectId
            ? projects.filter(p => p.projectId !== excludeProjectId)
            : projects;
        if (candidates.length > 0) return pickRandom(candidates);
        if (projects.length > 0) return pickRandom(projects);
    }
    return null;
}

// Load ALL released projects in background for dice (runs once, no blocking)
function loadAllProjectsInBackground() {
    if (cachedAllProjects || loadingAllProjects) return;
    loadingAllProjects = true;

    (async () => {
        const users = await fetchUsers();
        const all = [];
        // Fire all queries in parallel
        const promises = users.map(async (u) => {
            const q = query(collection(db, "user", u.uid, 'project'), where("release", "==", true));
            const snapshot = await getDocs(q);
            snapshot.forEach(doc => {
                all.push({ userid: u.uid, userName: u.name, projectId: doc.id, projectName: doc.data().name });
            });
        });
        await Promise.all(promises);
        cachedAllProjects = all;
    })().catch(err => {
        console.log(err);
        loadingAllProjects = false;
    });
}

const HomePage = () => {
    const user = useUser();
    const [value, setValue] = useState({
        finished: false,
        userid: '',
        userName: '',
        projectId: '',
        projectName: ''
    });
    const [gameKey, setGameKey] = useState(0);
    const [rolling, setRolling] = useState(false);
    const fetchedRef = useRef(false);

    const applyProject = useCallback((proj) => {
        if (!proj) return;
        setValue({
            finished: true,
            userid: proj.userid,
            userName: proj.userName,
            projectId: proj.projectId,
            projectName: proj.projectName
        });
        setGameKey(k => k + 1);
    }, []);

    // Initial load: fetch one random project fast, then preload all in background
    useEffect(() => {
        if (fetchedRef.current) return;
        fetchedRef.current = true;

        fetchOneRandomProject(null)
            .then(proj => {
                applyProject(proj);
                loadAllProjectsInBackground();
            })
            .catch(err => {
                console.log(err);
                fetchedRef.current = false;
            });
    }, [applyProject]);

    const rollGame = useCallback(async () => {
        setRolling(true);
        try {
            if (cachedAllProjects && cachedAllProjects.length > 0) {
                // Use cached list (no Firestore read)
                let candidates = cachedAllProjects.length > 1
                    ? cachedAllProjects.filter(p => p.projectId !== value.projectId)
                    : cachedAllProjects;
                if (candidates.length === 0) candidates = cachedAllProjects;
                applyProject(pickRandom(candidates));
            } else {
                // Fallback: fetch one random (1-2 queries)
                const proj = await fetchOneRandomProject(value.projectId);
                applyProject(proj);
            }
        } catch (err) {
            console.log(err);
        } finally {
            setRolling(false);
        }
    }, [value.projectId, applyProject]);

    return (
        <div className="container home">
            {value.projectId !== '' && (
                <GamePage key={gameKey} uid={value.userid} projectId={value.projectId} />)}
            <div className='home-panel'>
                <Info gameName={value.projectName} authorName={value.userName} />
                <Keyboard />
                <div className='home-btn'>
                    {value.finished &&
                        <div className='home-btn-play'>
                            <Link to={value.userid + '/' + value.projectId}>
                                Play
                            </Link>
                        </div>}
                    {user &&
                        <div className='home-btn-roll'>
                            <button onClick={rollGame} disabled={rolling}>
                                <i className="fa fa-random" aria-hidden="true"></i>
                            </button>
                        </div>}
                    {!user &&
                        <div className='home-btn-signup'>
                            <Link to={`/signup`}>
                                Sign Up
                            </Link>
                        </div>}
                </div>
            </div>
        </div>
    );
}

export default HomePage;

const Info = (props) => {
    return (
        <div className='home-info'>
            <h1>{props.gameName}</h1>
            <h3>{props.authorName}</h3>
        </div>
    )
}

const Keyboard = () => {
    const [leftArrow, setLeftArrow] = useState({});
    const [rightArrow, setRightArrow] = useState({});
    const [space, setSpace] = useState({});

    useEffect(() => {
        const keyDownStyle = { borderBottom: '0', marginTop: '4px', marginBottom: '0.5px' };
        const keyUpStyle = { borderBottom: '5px solid #c7c7c7e8' };

        const handleKeyDown = (e) => {
            if (e.key === "ArrowLeft") setLeftArrow(keyDownStyle);
            if (e.key === "ArrowRight") setRightArrow(keyDownStyle);
            if (e.key === " ") setSpace(keyDownStyle);
        };
        const handleKeyUp = (e) => {
            if (e.key === "ArrowLeft") setLeftArrow(keyUpStyle);
            if (e.key === "ArrowRight") setRightArrow(keyUpStyle);
            if (e.key === " ") setSpace(keyUpStyle);
        };

        window.addEventListener("keydown", handleKeyDown);
        window.addEventListener("keyup", handleKeyUp);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
            window.removeEventListener("keyup", handleKeyUp);
        };
    }, []);

    return (
        <div className='keyboard'>
            <div className='arrow arrow-left' style={leftArrow}>⇦</div>
            <div className='arrow arrow-right' style={rightArrow}>⇨</div>
            <div className='space' style={space}>Space</div>
        </div>
    )
}
